import { useQuery } from '@tanstack/react-query';
import { atom, useAtom } from 'jotai';

import { isServer } from '@flipster/utils/env-helper';

import { useCheckSpotOnly } from '../regulation';
import { getZendeskEmergenciesApi } from './api';
import { ZendeskArticle } from './types';
import { zendesk } from './url';

const testEmergencyArticle: ZendeskArticle = {
  id: 3600018173361,
  html_url: zendesk.getUrl('Notice'),
  created_at: '2020-08-26T06:44:45Z',
  section_id: 1,
  title: 'Test system maintenance is scheduled.',
  body: 'Test system maintenance is scheduled.',
};

const isEmergencyTestAtom = atom<boolean>(false);

export const ClosedEmergenciesStorageKey = 'flipster.closedEmergencies.v2';

export const getClosedEmergencies = () => {
  if (isServer) return undefined;
  const value = localStorage?.getItem(ClosedEmergenciesStorageKey);
  try {
    return value ? (JSON.parse(value) as number[]) : undefined;
  } catch {
    return undefined;
  }
};

export const setClosedEmergency = (id: number) => {
  const closedEmergencies = getClosedEmergencies() || [];
  const newClosedEmergencies = Array.from(new Set([...closedEmergencies, id])).slice(-5); // 최대 5개까지만 저장
  localStorage?.setItem(ClosedEmergenciesStorageKey, JSON.stringify(newClosedEmergencies));
};

export function useZendeskEmergency() {
  const [isEmergencyTest, setIsEmergencyTest] = useAtom(isEmergencyTestAtom);
  const isSpotOnly = useCheckSpotOnly();

  // 긴급 공지 로딩
  const { data: zendeskEmergency, refetch } = useQuery({
    queryKey: ['getZendeskEmergenciesApi', isSpotOnly],
    queryFn: () => getZendeskEmergenciesApi(isSpotOnly),
    select: (res) => {
      const closedEmergencies = getClosedEmergencies() || [];
      return res.data?.articles.find((ze) => !closedEmergencies.includes(ze.id)) || null;
    },
    enabled: !isEmergencyTest,
    refetchInterval: 5 * 60 * 1000, // 5분 단위
    refetchIntervalInBackground: true,
  });

  const setTestEmergency = () => {
    setIsEmergencyTest(true);
  };

  // 더이상 눈앞에 보이지 않도록 긴급 공지 닫음
  const closeEmergency = (id: number) => {
    if (isEmergencyTest) {
      setIsEmergencyTest(false);
    } else {
      setClosedEmergency(id);
    }
    refetch();
  };

  return {
    zendeskEmergency: isEmergencyTest ? testEmergencyArticle : zendeskEmergency,
    closeEmergency,
    setTestEmergency,
  };
}
